// Generated media: color mattes, gradients, SMPTE bars & tone, titles and a
// countdown leader. Nothing is stored but the spec — frames are drawn on demand
// at the sequence resolution (so transforms, fades and dissolves apply as with
// footage) and audio (tone / beeps) is synthesized into a buffer when needed.
import { GeneratorSpec, TextSpec } from "../state/model";

export type VisualSpec = Exclude<GeneratorSpec, { type: "tone" } | { type: "beeps" }>;
export type VisualType = VisualSpec["type"];

export const GENERATOR_LABELS: Record<GeneratorSpec["type"], string> = {
  color: "Color matte", gradient: "Gradient", bars: "Bars & tone", text: "Title", countdown: "Countdown", tone: "Tone", beeps: "Beeps",
};
/** Short badge shown on media-bin items. */
export const GENERATOR_BADGE: Record<GeneratorSpec["type"], string> = {
  color: "COLOR", gradient: "GRADIENT", bars: "BARS", text: "TITLE", countdown: "LEADER", tone: "TONE", beeps: "BEEPS",
};

export const FONTS = ["Segoe UI", "Arial", "Helvetica", "Georgia", "Times New Roman", "Verdana", "Trebuchet MS", "Impact", "Courier New"];

export const TEXT_PRESETS: Record<"title" | "lower" | "subtitle", Partial<TextSpec>> = {
  title: { text: "Title", size: 0.12, align: "center", x: 0.5, y: 0.5, bold: true, bg: "none", shadow: true, outline: 0 },
  lower: { text: "Name Surname\nTitle or role", size: 0.055, align: "left", x: 0.07, y: 0.82, bold: true, bg: "box", bgColor: "#101828", bgOpacity: 0.75, shadow: false, outline: 0 },
  subtitle: { text: "Subtitle text", size: 0.05, align: "center", x: 0.5, y: 0.9, bold: false, bg: "box", bgColor: "#000000", bgOpacity: 0.6, shadow: false, outline: 0 },
};

export function defaultSpec(type: VisualType): VisualSpec {
  switch (type) {
    case "color": return { type, color: "#000000" };
    case "gradient": return { type, from: "#1e3a8a", to: "#7c3aed", angle: 135 };
    case "bars": return { type, tone: true, freq: 1000, db: -20 };
    case "countdown": return { type, from: 5, bg: "#1f2937", accent: "#e5e7eb", beep: true };
    case "text": return {
      type, text: "Title", font: "Segoe UI", size: 0.12, color: "#ffffff", bold: true, italic: false, align: "center",
      x: 0.5, y: 0.5, outline: 0, outlineColor: "#000000", shadow: true, bg: "none", bgColor: "#000000", bgOpacity: 0.6,
    };
  }
}

/** Media length (s) and the default clip length for a new generated item. */
export function generatorDurations(spec: VisualSpec): { duration: number; defaultDuration: number } {
  if (spec.type === "countdown") return { duration: spec.from, defaultDuration: spec.from };
  if (spec.type === "bars") return { duration: 60, defaultDuration: 10 }; // tone buffer is 60 s
  return { duration: 3600, defaultDuration: 5 };                         // static: trim as long as you like
}

/** Animated generators must be redrawn per frame; static ones are cached. */
export const isAnimated = (spec: GeneratorSpec) => spec.type === "countdown";

/** Draw a generator frame at time `t` (seconds into the media) into a w×h area. */
export function drawGenerator(g: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, spec: GeneratorSpec, w: number, h: number, t = 0) {
  g.save();
  switch (spec.type) {
    case "color":
      g.fillStyle = spec.color; g.fillRect(0, 0, w, h);
      break;
    case "gradient": {
      const a = (spec.angle * Math.PI) / 180, r = Math.hypot(w, h) / 2;
      const dx = Math.cos(a) * r, dy = Math.sin(a) * r;
      const grad = g.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy);
      grad.addColorStop(0, spec.from); grad.addColorStop(1, spec.to);
      g.fillStyle = grad; g.fillRect(0, 0, w, h);
      break;
    }
    case "bars": drawBars(g, w, h); break;
    case "text": drawText(g, spec, w, h); break;
    case "countdown": drawCountdown(g, spec, w, h, t); break;
    default: g.fillStyle = "#000"; g.fillRect(0, 0, w, h);
  }
  g.restore();
}

/** SMPTE-style color bars (75% bars, castellations, -I / white / +Q, PLUGE). */
function drawBars(g: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, w: number, h: number) {
  const top = ["#c0c0c0", "#c0c000", "#00c0c0", "#00c000", "#c000c0", "#c00000", "#0000c0"];
  const mid = ["#0000c0", "#131313", "#c000c0", "#131313", "#00c0c0", "#131313", "#c0c0c0"];
  const bw = w / 7, h1 = Math.round(h * 0.67), h2 = Math.round(h * 0.08);
  top.forEach((c, i) => { g.fillStyle = c; g.fillRect(Math.floor(i * bw), 0, Math.ceil(bw) + 1, h1); });
  mid.forEach((c, i) => { g.fillStyle = c; g.fillRect(Math.floor(i * bw), h1, Math.ceil(bw) + 1, h2); });
  const y3 = h1 + h2, h3 = h - y3;
  const q = (bw * 5) / 4; // bottom row: four wide patches across the first five bars
  const bottom: [string, number][] = [["#00214c", q], ["#ffffff", q], ["#32006a", q], ["#131313", q]];
  let x = 0;
  for (const [c, wd] of bottom) { g.fillStyle = c; g.fillRect(Math.floor(x), y3, Math.ceil(wd) + 1, h3); x += wd; }
  // PLUGE under bar 6: slightly below black / black / slightly above black
  const pw = bw / 3;
  for (const [i, c] of (["#090909", "#131313", "#1d1d1d"] as const).entries()) { g.fillStyle = c; g.fillRect(Math.floor(5 * bw + i * pw), y3, Math.ceil(pw) + 1, h3); }
  g.fillStyle = "#131313"; g.fillRect(Math.floor(6 * bw), y3, Math.ceil(bw) + 1, h3);
}

function hexA(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.max(0, Math.min(1, a))})`;
}

function drawText(g: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, s: TextSpec, w: number, h: number) {
  const px = Math.max(4, s.size * h);
  g.font = `${s.italic ? "italic " : ""}${s.bold ? "700" : "400"} ${px}px "${s.font}", system-ui, sans-serif`;
  g.textBaseline = "middle";
  g.textAlign = s.align;
  const lines = (s.text || "").split("\n");
  const lh = px * 1.2;
  const blockH = lh * lines.length;
  const cy = s.y * h, ax = s.x * w;
  const widths = lines.map((l) => g.measureText(l).width);
  const maxW = Math.max(0, ...widths);
  if (s.bg === "full") { g.fillStyle = hexA(s.bgColor, s.bgOpacity); g.fillRect(0, 0, w, h); }
  if (s.bg === "box" && maxW > 0) {
    const pad = px * 0.35;
    const left = s.align === "left" ? ax : s.align === "right" ? ax - maxW : ax - maxW / 2;
    g.fillStyle = hexA(s.bgColor, s.bgOpacity);
    g.fillRect(left - pad, cy - blockH / 2 - pad * 0.6, maxW + pad * 2, blockH + pad * 1.2);
  }
  lines.forEach((line, i) => {
    const y = cy - blockH / 2 + lh * (i + 0.5);
    if (s.outline > 0) {
      g.lineJoin = "round";
      g.lineWidth = s.outline * px;
      g.strokeStyle = s.outlineColor;
      g.strokeText(line, ax, y);
    }
    if (s.shadow) { g.shadowColor = "rgba(0,0,0,0.65)"; g.shadowBlur = px * 0.12; g.shadowOffsetX = px * 0.04; g.shadowOffsetY = px * 0.05; }
    g.fillStyle = s.color;
    g.fillText(line, ax, y);
    g.shadowColor = "transparent"; g.shadowBlur = 0; g.shadowOffsetX = 0; g.shadowOffsetY = 0;
  });
}

/** Film-leader style countdown: a sweeping wedge, crosshair rings and the number. */
function drawCountdown(g: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, s: { from: number; bg: string; accent: string }, w: number, h: number, t: number) {
  g.fillStyle = s.bg; g.fillRect(0, 0, w, h);
  const n = Math.max(1, Math.ceil(s.from - Math.max(0, t) - 1e-6));
  const frac = Math.max(0, t) % 1; // progress through the current second
  const cx = w / 2, cy = h / 2, r = Math.min(w, h) * 0.38;
  g.fillStyle = hexA(s.accent, 0.18);
  g.beginPath(); g.moveTo(cx, cy); g.arc(cx, cy, r * 1.6, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2); g.closePath(); g.fill();
  g.strokeStyle = s.accent; g.lineWidth = Math.max(1, h * 0.006);
  for (const k of [1, 0.82]) { g.beginPath(); g.arc(cx, cy, r * k, 0, Math.PI * 2); g.stroke(); }
  g.beginPath(); g.moveTo(0, cy); g.lineTo(w, cy); g.moveTo(cx, 0); g.lineTo(cx, h); g.stroke();
  g.fillStyle = s.accent;
  g.font = `700 ${Math.round(r * 1.25)}px "Segoe UI", system-ui, sans-serif`;
  g.textAlign = "center"; g.textBaseline = "middle";
  g.fillText(String(n), cx, cy + r * 0.05);
}

// ---- frame cache --------------------------------------------------------------
interface CacheEntry { cv: HTMLCanvasElement; key: string; }
const frameCache = new Map<string, CacheEntry>(); // per asset (+ consumer)

/** A canvas holding the generator's frame at `t`, rendered at w×h. Static
 *  generators are redrawn only when their spec or size changes. */
export function generatorFrame(cacheId: string, spec: GeneratorSpec, w: number, h: number, t: number, fps = 30): HTMLCanvasElement {
  const key = JSON.stringify(spec) + `|${w}x${h}` + (isAnimated(spec) ? `|${Math.floor(t * fps)}` : "");
  let e = frameCache.get(cacheId);
  if (e && e.key === key) return e.cv;
  if (!e) { const cv = document.createElement("canvas"); e = { cv, key: "" }; frameCache.set(cacheId, e); }
  if (e.cv.width !== w || e.cv.height !== h) { e.cv.width = w; e.cv.height = h; }
  const g = e.cv.getContext("2d")!;
  g.clearRect(0, 0, w, h);
  drawGenerator(g, spec, w, h, t);
  e.key = key;
  return e.cv;
}
export function dropGeneratorCache(assetId: string) {
  for (const k of frameCache.keys()) if (k === assetId || k.startsWith(assetId + "|")) frameCache.delete(k);
}

// ---- audio ---------------------------------------------------------------------
/** Synthesize a generated audio asset (tone or countdown beeps). */
export function synthAudio(spec: GeneratorSpec, seconds: number, sampleRate = 48000): AudioBuffer | null {
  if (spec.type !== "tone" && spec.type !== "beeps") return null;
  const len = Math.max(1, Math.round(seconds * sampleRate));
  const buf = new AudioBuffer({ numberOfChannels: 1, length: len, sampleRate });
  const d = buf.getChannelData(0);
  const amp = Math.pow(10, spec.db / 20);
  const w = (2 * Math.PI * spec.freq) / sampleRate;
  if (spec.type === "tone") {
    for (let i = 0; i < len; i++) d[i] = amp * Math.sin(w * i);
  } else {
    // a 60 ms beep at the start of every second (5 ms ramps, no clicks)
    const beep = Math.round(0.06 * sampleRate), ramp = Math.round(0.005 * sampleRate);
    for (let s = 0; s < spec.count; s++) {
      const o = s * sampleRate;
      for (let i = 0; i < beep && o + i < len; i++) {
        const env = Math.min(1, i / ramp, (beep - i) / ramp);
        d[o + i] = amp * env * Math.sin(w * i);
      }
    }
  }
  return buf;
}
